import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {EventsService} from "../../../events/events.service";
import {EventCategoryListItem} from "../../../events/types/event-category-list-item";
import {SlickCarouselComponent, SlickCarouselModule} from "ngx-slick-carousel";
import {CategoryFilterItem} from "./types/category-filter-item";
import {Event, EventArtist} from "../../../events/types/event";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {EventFilters} from "./types/event-filters";
import {EventsFilterResponse} from "../../../events/types/events-filter-response";
import {EventsCalendar} from "./types/events-calendar";
import {environment} from "../../../../environments/environment";
import {WebpImageComponent} from "../../../components/webp-image/webp-image.component";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-widget-events-calendar',
  standalone: true,
  imports: [CommonModule, SlickCarouselModule, RouterLink, WebpImageComponent],
  templateUrl: './events-calendar.component.html',
  styleUrls: [
    './events-calendar.component.less',
    '../../../../styles/events/_events-list.less'
  ]
})
export class EventsCalendarComponent implements OnInit, AfterViewInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private initialQueryParamsApplied = false;

  @Input() widget !: EventsCalendar;
  @ViewChild('yearsSlider') yearsSlider!: SlickCarouselComponent;
  @ViewChild('datesSlider') datesSlider!: SlickCarouselComponent;

  public slideConfig = {
    infinite: false,
    slidesToShow: 10,
    slidesToScroll: 1,
    responsive: [{
      breakpoint: 1024, settings: {
        slidesToShow: 6,
      }
    }, {
      breakpoint: 600, settings: {
        slidesToShow: 4,
      }
    }, {
      breakpoint: 480, settings: {
        slidesToShow: 3,
      }
    }]
  };

  public yearsSlideConfig = {
    infinite: false,
    slidesToShow: 'auto',
    arrows: false,
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 480,
        settings: {
          centerMode: true,
          variableWidth: true,
          centerPadding: '20px',
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }]
  };

  public categories!: CategoryFilterItem[];
  public events!: Event[];
  public eventDates!: string[];
  public viewMode!: 'list' | 'grid';
  public selectedDay: string | null = null;
  public selectedYear: number | null = null;
  private filtersUnchanged: boolean = true;

  private eventFilters: EventFilters = {
    categoryIds: [],
    dateFrom: `${new Date().getFullYear()}-01-01 00:00:00`,
    dateTo: `${new Date().getFullYear()}-12-31 23:59:59`
  };

  protected trackByFn(index: number, item: string) {
    return index;
  };

  public constructor(
    private eventsService: EventsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.viewMode = this.widget.data.view ?? 'list';

    // Determine initial year and filters based on widget configuration
    let initialDate: Date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (this.widget.data.initialDateMode === 'specific' && this.widget.data.initialDate) {
      const configuredDate = new Date(this.widget.data.initialDate);
      configuredDate.setHours(0, 0, 0, 0);

      // If the configured date is in the past, use today instead
      if (configuredDate >= today) {
        initialDate = configuredDate;
      } else {
        initialDate = today;
      }
    } else {
      initialDate = today;
    }

    this.selectedYear = initialDate.getFullYear();

    // Set up initial event filters for the year containing the initial date
    this.eventFilters = {
      categoryIds: [],
      dateFrom: `${this.selectedYear}-01-01 00:00:00`,
      dateTo: `${this.selectedYear}-12-31 23:59:59`
    };

    // Apply query parameters if present
    const queryParams = this.route.snapshot.queryParams;
    this.applyQueryParams(queryParams);

    this.eventsService.getCategoriesList()
      .subscribe(
        (categories: EventCategoryListItem[]) => {
          this.categories = categories.map((c) => {
            return {
              category: c,
              selected: false,
            }
          });

          // Apply category filters from query params after categories are loaded
          if (queryParams['categories']) {
            this.applyCategoryFilters(queryParams['categories']);
            this.loadEvents();
          }

          this.initialQueryParamsApplied = true;

          // Subscribe to query param changes for back/forward navigation
          this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
              if (this.initialQueryParamsApplied) {
                this.applyQueryParamsFromNavigation(params);
              }
            });
        }
      );

    this.loadEvents();
  }

  private applyCategoryFilters(categoriesParam: string): void {
    const categoryIds = categoriesParam.split(',').map((id: string) => parseInt(id, 10));
    this.categories.forEach(c => {
      c.selected = categoryIds.includes(c.category.id);
    });
    this.eventFilters.categoryIds = categoryIds;
  }

  private applyQueryParamsFromNavigation(params: {[key: string]: string}): void {
    let needsReload = false;

    // Determine default year based on widget configuration
    const defaultYear = this.getDefaultYear();

    // Apply year filter (use default if not in URL)
    const yearParam = params['year'] ? parseInt(params['year'], 10) : defaultYear;
    const targetYear = this.widget.data.years.includes(yearParam) ? yearParam : defaultYear;

    if (targetYear !== this.selectedYear) {
      this.selectedYear = targetYear;
      this.eventFilters.dateFrom = `${targetYear}-01-01 00:00:00`;
      this.eventFilters.dateTo = `${targetYear}-12-31 23:59:59`;
      this.filtersUnchanged = !params['year'];
      needsReload = true;

      // Update year slider position
      setTimeout(() => {
        this.yearsSlider?.slickGoTo(this.widget.data.years.findIndex(y => y === this.selectedYear));
      });
    }

    // Apply day filter (null if not in URL)
    const dayParam = params['day'] ?? null;
    if (dayParam !== this.selectedDay) {
      this.selectedDay = dayParam;
      if (dayParam) {
        this.filtersUnchanged = false;
      }
    }

    // Apply view mode (use widget default if not in URL)
    const defaultViewMode = this.widget.data.view ?? 'list';
    const viewParam = params['view'];
    const targetViewMode = (viewParam === 'list' || viewParam === 'grid') ? viewParam : defaultViewMode;
    if (targetViewMode !== this.viewMode) {
      this.viewMode = targetViewMode;
    }

    // Apply category filters (empty if not in URL)
    const categoriesParam = params['categories'];
    const currentCategoryIds = (this.eventFilters.categoryIds ?? []).join(',');
    if ((categoriesParam ?? '') !== currentCategoryIds) {
      if (categoriesParam) {
        this.applyCategoryFilters(categoriesParam);
      } else {
        this.categories.forEach(c => c.selected = false);
        this.eventFilters.categoryIds = [];
      }
      needsReload = true;
    }

    if (needsReload) {
      this.loadEvents();
    }
  }

  private getDefaultYear(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (this.widget.data.initialDateMode === 'specific' && this.widget.data.initialDate) {
      const configuredDate = new Date(this.widget.data.initialDate);
      configuredDate.setHours(0, 0, 0, 0);
      if (configuredDate >= today) {
        return configuredDate.getFullYear();
      }
    }
    return today.getFullYear();
  }

  private applyQueryParams(params: {[key: string]: string}): void {
    // Apply year/season filter
    if (params['year']) {
      const year = parseInt(params['year'], 10);
      if (this.widget.data.years.includes(year)) {
        this.selectedYear = year;
        this.eventFilters.dateFrom = `${year}-01-01 00:00:00`;
        this.eventFilters.dateTo = `${year}-12-31 23:59:59`;
        this.filtersUnchanged = false;
      }
    }

    // Apply day filter
    if (params['day']) {
      this.selectedDay = params['day'];
      this.filtersUnchanged = false;
    }

    // Apply view mode
    if (params['view'] && (params['view'] === 'list' || params['view'] === 'grid')) {
      this.viewMode = params['view'];
    }
  }

  ngAfterViewInit() {
    this.yearsSlider.slickGoTo(
      this.widget.data.years.findIndex(y => y === this.selectedYear),
    );
  }

  private loadEvents(): void {
    this.eventsService.getEvents(
      this.eventFilters.categoryIds,
      this.eventFilters.dateFrom,
      this.eventFilters.dateTo,
    )
      .subscribe(
        (response: EventsFilterResponse) => {
          this.events = response.items;
          this.eventDates = response.days;

          // Scroll to next upcoming date after dates are loaded
          setTimeout(() => this.scrollToNextUpcomingDate(), 100);
        }
      );
  }

  private scrollToNextUpcomingDate(): void {
    if (!this.datesSlider || !this.eventDates || this.eventDates.length === 0) {
      return;
    }

    // Determine which date to use based on widget configuration
    let targetDate: Date;

    if (this.widget.data.initialDateMode === 'specific' && this.widget.data.initialDate) {
      // Use the specific date from widget configuration
      targetDate = new Date(this.widget.data.initialDate);
      targetDate.setHours(0, 0, 0, 0);
    } else {
      // Default to today's date
      targetDate = new Date();
      targetDate.setHours(0, 0, 0, 0);
    }

    // Find the index of the first date that matches or is after the target date
    const nextDateIndex = this.eventDates.findIndex(dateString => {
      const eventDate = new Date(dateString);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= targetDate;
    });

    // If we found a matching date, scroll to it
    if (nextDateIndex !== -1) {
      // Calculate how many slides are visible based on current viewport
      const slidesToShow = this.getSlidesToShow();

      // Adjust the target index to account for the fact that slick can't scroll
      // past the point where the last slide is visible
      const maxScrollIndex = Math.max(0, this.eventDates.length - slidesToShow);
      const targetIndex = Math.min(nextDateIndex, maxScrollIndex);

      this.datesSlider.slickGoTo(targetIndex);
    }
  }

  private getSlidesToShow(): number {
    const width = window.innerWidth;

    // Match the breakpoints from slideConfig
    if (width < 480) {
      return 3;  // slidesToShow: 3
    } else if (width < 600) {
      return 4;  // slidesToShow: 4
    } else if (width < 1024) {
      return 6;  // slidesToShow: 6
    } else {
      return 10; // slidesToShow: 10
    }
  }

  public getCategoryButtonClasses(category: EventCategoryListItem): string[] {
    return [
      `button-event-category-style--${category.style}`
    ]
  }

  public getEventArtists(event: Event): string {
    return event.artists.filter(m => m.name).map(a => this.getArtistText(a)).join(', ');
  }

  private getArtistText(artist: EventArtist) {
    if (!artist.isGroup) {
      return artist.name.trim();
    }

    return `<strong>${artist.name}</strong> | ${artist.members.filter(m => m.name).map(m => m.name.trim()).join(', ')}`;
  }

  public getEventStyle(event: Event): string[] {
    return [
      `event-style--${event.style}`
    ];
  }

  public getEventImageUrl(event: Event): string {
    return `${environment.cdn.url}/events/thumbnails/medium/${event.image}`;
  }

  public getEventCopyrights(event: Event): string {
    return event.copyrights || event.name;
  }

  public getEventDisplayName(event: Event): string {
    return event.name;
  }

  public filterBySelectedDate(event: Event): boolean {
    if (this.noFilters()) {
      const eventDate = new Date(event.startTime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (eventDate < today) {
        return false;
      }
    }
    return this.selectedDay === null || event.startTime.startsWith(this.selectedDay);
  }

  public onClickCategoryFilter(category: CategoryFilterItem): void {
    this.filtersUnchanged = false;
    category.selected = !category.selected;
    this.eventFilters.categoryIds = this.categories.filter(c => c.selected).map(c => c.category.id);
    this.loadEvents();
    this.updateQueryParams();
  }

  public onClickYearFilter(year: number) {
    this.filtersUnchanged = false;
    this.eventFilters.dateFrom = `${year}-01-01`;
    this.eventFilters.dateTo = `${year}-12-31 23:59:59`;
    this.selectedDay = null;
    this.selectedYear = year;
    this.loadEvents();
    this.updateQueryParams();
  }

  public onClickEventDay(day: string): void {
    if (this.selectedDay === day) {
      this.selectedDay = null;
    } else {
      this.selectedDay = day;
    }
    this.updateQueryParams();
  }

  public setViewMode(value: 'list' | 'grid'): void {
    this.viewMode = value;
    this.updateQueryParams();
  }

  private updateQueryParams(): void {
    const categoryIds = this.eventFilters.categoryIds ?? [];
    const queryParams: {[key: string]: string | null} = {
      year: this.selectedYear?.toString() ?? null,
      day: this.selectedDay,
      view: this.viewMode !== this.widget.data.view ? this.viewMode : null,
      categories: categoryIds.length > 0 ? categoryIds.join(',') : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private noFilters(): boolean {
    return this.filtersUnchanged && !this.selectedDay;
  }

  public hasEventStarted(event: Event): boolean {
    return new Date(event.startTime) < new Date();
  }
}
